import { ExtendedTreeNodeType } from '@features/campaigns/builder/components/FormFields/common/Tree/types/treeTypes';
import { DropdownItem } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { ContentPackageStatus } from '@features/campaigns/builder/components/FormFields/Step02/FieldContentPackage/types.ts';
import { GeoApiRegion } from '@features/campaigns/builder/components/FormFields/Step02/FieldGeo/FieldGeo.types';
import { apiRoutes } from '@features/campaigns/builder/consts/apiRoutes.ts';
import { apiClient } from '@shared/api/ApiClient.ts';
import { AdManagerApiResponse } from '@shared/api/commonApiInterfaces.ts';
import { apiRoutes as sharedApiRoutes } from '@shared/consts/apiRoutes.ts';
import { CampaignId } from '@shared/types/campaign.ts';
import { Status } from '@shared/types/common';
import { UserCountryCode } from '@shared/types/countryCodes.ts';
import { UserId } from '@shared/types/users.ts';

const PER_PAGE_LIMIT = 30;

export interface Item {
  id: string;
  name: string;
  status: Status;
  isPreliminaryBlocked?: boolean;
  code: string;
}

export function mapResponseToFieldData(
  response: AdManagerApiResponse<Item[]> | null,
): AdManagerApiResponse<DropdownItem[]> | null {
  if (response === null) {
    return response;
  }

  const mappedFields = response.items.map(
    ({ name, id, code, isPreliminaryBlocked = false, status }) =>
      ({
        name,
        value: id,
        disabled: isPreliminaryBlocked,
        code,
        nameWithCode: `${name} (${code})`,
        status,
      }) as const,
  );

  return {
    items: mappedFields,
    meta: response.meta,
  };
}

function mapProvincesResponseToFieldData(
  response: AdManagerApiResponse<GeoApiRegion[]> | null,
): AdManagerApiResponse<GeoApiRegion[]> | null {
  if (!response) {
    return null;
  }

  const mappedFields = response.items.map((item: GeoApiRegion) => ({
    id: item.id,
    name: item.name,
    provinces: item.provinces,
  }));

  return {
    items: mappedFields,
    meta: response.meta,
  };
}

function mapTargetingItemsResponseToFieldData(
  response: AdManagerApiResponse<ExtendedTreeNodeType[]> | null,
): AdManagerApiResponse<ExtendedTreeNodeType[]> | null {
  if (!response) {
    return null;
  }

  const mappedFields = response.items.map((item) => ({
    name: item.name,
    id: item.id,
    status: item.status,
    isAiBased: item.isAiBased,
  }));

  return {
    items: mappedFields,
    meta: response.meta,
  };
}

export interface DefaultQueryParams {
  page: number;
  search: string | undefined;
}

const defaultPageParam: DefaultQueryParams = {
  page: 1,
  search: '',
};

interface DefaultTargetingQueryParams {
  page?: number;
  search?: string;
  limit?: number;
}

const defaultTargetingPageParam: DefaultTargetingQueryParams = {
  page: 1,
  search: '',
  limit: 10000,
};

export async function fetchAdvertisers({
  pageParam = defaultPageParam,
}): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(apiRoutes.getAdvertisers(), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
    })
    .then((data) => mapResponseToFieldData(data));
}

export async function fetchAdvertisersAssignedToUser(
  userId: UserId,
  { pageParam = defaultPageParam },
  onlyActiveAdvertisers?: boolean,
): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(sharedApiRoutes.getAdvertisersAssignedToUser(userId), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
      includeInactive: onlyActiveAdvertisers ? !onlyActiveAdvertisers : undefined,
    })
    .then((data) => mapResponseToFieldData(data));
}

export async function fetchBrands(
  advertiserId: string,
  { pageParam = defaultPageParam },
): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(apiRoutes.getBrands(advertiserId), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
    })
    .then((data) => mapResponseToFieldData(data));
}

export async function fetchContentPackages({
  campaignId,
  status,
}: {
  campaignId: CampaignId;
  status: ContentPackageStatus;
}): Promise<AdManagerApiResponse<ExtendedTreeNodeType[]> | null> {
  return await apiClient
    .get<
      AdManagerApiResponse<ExtendedTreeNodeType[]>
    >(apiRoutes.getContentPackages(), { ...defaultTargetingPageParam, campaignId, status })
    .then((data) => mapTargetingItemsResponseToFieldData(data));
}

export async function fetchAgeAndGenderGroups(
  countryCode: UserCountryCode,
): Promise<AdManagerApiResponse<ExtendedTreeNodeType[]> | null> {
  return await apiClient
    .get<AdManagerApiResponse<ExtendedTreeNodeType[]>>(apiRoutes.getAgeAndGenderGroups(), {
      ...defaultTargetingPageParam,
      country: countryCode,
    })
    .then((data) => mapTargetingItemsResponseToFieldData(data));
}

export async function fetchInterestSegments(
  countryCode: UserCountryCode,
): Promise<AdManagerApiResponse<ExtendedTreeNodeType[]> | null> {
  return await apiClient
    .get<AdManagerApiResponse<ExtendedTreeNodeType[]>>(apiRoutes.getInterestSegments(), {
      ...defaultTargetingPageParam,
      country: countryCode,
    })
    .then((data) => mapTargetingItemsResponseToFieldData(data));
}

export async function fetchGeoData(countryCode: UserCountryCode): Promise<AdManagerApiResponse<GeoApiRegion[]> | null> {
  return await apiClient
    .get<
      AdManagerApiResponse<GeoApiRegion[]>
    >(apiRoutes.getGeoData(), { ...defaultTargetingPageParam, country: countryCode })
    .then((data) => mapProvincesResponseToFieldData(data));
}

interface BillingContactsRequiredParams {
  advertiserId: string;
  campaignId?: CampaignId;
}

interface BillingContactsRequestParams {
  reqParam: BillingContactsRequiredParams;
  pageParam: DefaultQueryParams;
}

export async function fetchBillingContacts({
  reqParam,
  pageParam = defaultPageParam,
}: BillingContactsRequestParams): Promise<AdManagerApiResponse<DropdownItem[]> | null> {
  const { page, search } = pageParam;
  const { advertiserId, campaignId } = reqParam;

  return await apiClient
    .get<AdManagerApiResponse<Item[]>>(apiRoutes.getBillingContacts(advertiserId), {
      limit: PER_PAGE_LIMIT,
      page,
      search,
      campaignId,
    })
    .then((data) => mapResponseToFieldData(data));
}
